import { useEffect, useState } from 'react';
import {
  TargetPlatform,
  MacArchitecture,
  PlatformBuildInfo,
  downloadBuild,
} from '../services/builds';
import { DisplayMode } from '../models/project';
import { actionFailureMessage } from '../utils/actionFailure';
import { IconX, IconArrowL, IconUpgrade } from './Icons';
import InlineError from './InlineError';

interface BuildsDialogProps {
  projectId: string;
  projectVersion: string;
  displayMode: DisplayMode | undefined;
  isChatRunning: boolean;
  engineUpgradeRequired: boolean;
  onUpgradeEngine: () => void;
  builds: Record<string, PlatformBuildInfo>;
  buildsLoading: boolean;
  refreshBuilds: () => Promise<void>;
  /** Rejects when the build could not be started; the dialog shows why next to that platform. */
  onBuild: (platform: TargetPlatform) => Promise<void>;
  onClose: () => void;
  onGoToVersionControl: () => void;
}

const PLATFORM_META: Record<TargetPlatform, { name: string; extension: string } | undefined> = {
  Windows: { name: 'Windows', extension: '.exe' },
  Mac: { name: 'macOS', extension: '.dmg' },
  WebGL: { name: 'Web', extension: '.html5' },
  Android: { name: 'Android', extension: '.apk' },
  Ios: { name: 'iOS', extension: '.ipa' },
  Linux: { name: 'Linux', extension: '.app' },
  PlayStation: undefined,
  Xbox: undefined,
  Switch: undefined,
};

function platformsForDisplayMode(displayMode: DisplayMode | undefined): TargetPlatform[] {
  switch (displayMode) {
    case 'MobilePortrait':
    case 'MobileLandscape':
      return ['Android', 'Ios'];
    case 'PC':
      return ['Windows', 'Mac', 'WebGL'];
    default:
      return [];
  }
}

function compareSemver(a: string | null | undefined, b: string | null | undefined): number {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const da = pa[i] || 0;
    const db = pb[i] || 0;
    if (da !== db) return da - db;
  }
  return 0;
}

function formatSize(bytes: number | null): string {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

export default function BuildsDialog({
  projectId,
  projectVersion,
  displayMode,
  isChatRunning,
  engineUpgradeRequired,
  onUpgradeEngine,
  builds,
  buildsLoading,
  refreshBuilds: _refreshBuilds,
  onBuild,
  onClose,
  onGoToVersionControl,
}: BuildsDialogProps) {
  const [busyPlatform, setBusyPlatform] = useState<TargetPlatform | null>(null);
  const [busyAction, setBusyAction] = useState<'download' | null>(null);
  const [macArchPrompt, setMacArchPrompt] = useState(false);
  const [comingSoonPlatform, setComingSoonPlatform] = useState<TargetPlatform | null>(null);
  const [failures, setFailures] = useState<Partial<Record<TargetPlatform, string>>>({});

  const showFailure = (platform: TargetPlatform, message: string | null) =>
    setFailures((previous) => ({ ...previous, [platform]: message ?? undefined }));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleDownload = async (platform: TargetPlatform, architecture?: MacArchitecture) => {
    if (platform === 'Mac' && !architecture) {
      setMacArchPrompt(true);
      return;
    }
    showFailure(platform, null);
    try {
      setBusyPlatform(platform);
      setBusyAction('download');
      await downloadBuild(projectId, platform, architecture);
      setMacArchPrompt(false);
    } catch (error) {
      showFailure(platform, actionFailureMessage(error, `Failed to download the ${PLATFORM_META[platform]?.name} build.`));
    } finally {
      setBusyPlatform(null);
      setBusyAction(null);
    }
  };

  const handleBuild = (platform: TargetPlatform) => {
    if (platform === 'Ios') {
      setComingSoonPlatform(platform);
      return;
    }
    showFailure(platform, null);
    onBuild(platform).catch((error: unknown) =>
      showFailure(platform, actionFailureMessage(error, `Failed to start the ${PLATFORM_META[platform]?.name} build.`)),
    );
  };

  const supportedPlatforms = platformsForDisplayMode(displayMode).filter((p) => PLATFORM_META[p]);
  const projectIsZero = projectVersion === '0.0.0';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-panel"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Builds</div>
            <div className="display tight" style={{ fontSize: 22 }}>Targets &amp; downloads</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              Project version <span className="mono">v{projectVersion}</span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="btn ghost sm icon" aria-label="Close">
            <IconX />
          </button>
        </div>

        {macArchPrompt && (
          <div style={{ marginBottom: 18 }}>
            <button
              type="button"
              onClick={() => setMacArchPrompt(false)}
              className="nav-link"
              style={{ padding: 0, height: 'auto', marginBottom: 10 }}
            >
              <IconArrowL size={14} /> Back
            </button>
            <div className="muted-2" style={{ fontSize: 13, marginBottom: 10 }}>
              Choose your Mac processor type:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button
                type="button"
                onClick={() => handleDownload('Mac', 'arm64')}
                disabled={busyPlatform === 'Mac'}
                className="btn"
              >
                Apple Silicon (M1+)
              </button>
              <button
                type="button"
                onClick={() => handleDownload('Mac', 'x64')}
                disabled={busyPlatform === 'Mac'}
                className="btn"
              >
                Intel
              </button>
            </div>
            {failures.Mac && (
              <div style={{ marginTop: 8 }}>
                <InlineError>{failures.Mac}</InlineError>
              </div>
            )}
          </div>
        )}

        {!macArchPrompt && engineUpgradeRequired && (
          <div
            role="status"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              padding: '12px 14px',
              marginBottom: 14,
              border: '1px solid var(--accent-line)',
              background: 'var(--accent-wash-2)',
              borderRadius: 10,
            }}
          >
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>Upgrade the engine first</div>
              <div className="muted-2" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                This game was made with an older engine version, so it can’t be built until the engine is upgraded.
                The upgrade runs in a new chat.
              </div>
            </div>
            <button
              type="button"
              className="btn primary sm"
              onClick={onUpgradeEngine}
              disabled={isChatRunning}
              title={isChatRunning ? 'Wait for the running chat to finish' : undefined}
            >
              <IconUpgrade size={14} />
              Upgrade engine
            </button>
          </div>
        )}

        {!macArchPrompt && (
          <>
            {buildsLoading && Object.keys(builds).length === 0 ? (
              <div style={{ display: 'grid', placeItems: 'center', padding: '40px 0' }} className="muted">
                Loading builds…
              </div>
            ) : supportedPlatforms.length === 0 ? (
              <div className="muted" style={{ textAlign: 'center', padding: '24px 0', fontSize: 13 }}>
                No supported platforms for this display mode.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {supportedPlatforms.map((platform) => {
                  const meta = PLATFORM_META[platform]!;
                  const info = builds[platform];
                  const buildState = info?.state ?? 'NotBuilt';
                  const buildVersion = info?.version ?? null;
                  const isThisBusy = busyPlatform === platform;
                  const isBuildLoading = buildState === 'Building';
                  const upToDate = info && compareSemver(buildVersion, projectVersion) >= 0;
                  const supersededSoon = isChatRunning && info && upToDate;
                  const canBuild =
                    (buildState === 'NotBuilt' || (buildState === 'Ready' && !upToDate) || buildState === 'Failed') &&
                    !projectIsZero;

                  return (
                    <div
                      key={platform}
                      role="group"
                      aria-label={meta.name}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        padding: '12px 14px',
                        background: 'var(--bg-2)',
                        border: '1px solid var(--line)',
                        borderRadius: 10,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, fontSize: 13 }}>{meta.name}</span>
                            <span className="mono muted" style={{ fontSize: 11 }}>{meta.extension}</span>
                            {buildVersion && (
                              <span className="mono" style={{ fontSize: 11, color: 'var(--accent)' }}>v{buildVersion}</span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-5)', marginTop: 4 }}>
                            {buildState === 'Ready' && info?.sizeBytes && <span>{formatSize(info.sizeBytes)}</span>}
                            {buildState === 'Building' && <span style={{ color: 'var(--warn)' }}>Building…</span>}
                            {buildState === 'Failed' && (
                              <span style={{ color: 'var(--danger)' }}>
                                Build failed{info?.errorMessage ? `: ${info.errorMessage}` : ''}
                              </span>
                            )}
                            {buildState === 'NotBuilt' && !projectIsZero && <span>No build yet</span>}
                            {supersededSoon && (
                              <span style={{ marginLeft: 8, color: 'var(--text-5)' }}>· new version building soon</span>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {projectIsZero && buildState === 'NotBuilt' ? (
                            <span className="muted" style={{ fontSize: 11, fontStyle: 'italic' }}>Build something first</span>
                          ) : isBuildLoading ? (
                            <button type="button" disabled className="btn sm">
                              Building…
                            </button>
                          ) : (
                            <>
                              {buildState === 'Ready' && (
                                <button
                                  type="button"
                                  onClick={() => handleDownload(platform)}
                                  disabled={isThisBusy}
                                  className="btn primary sm"
                                >
                                  {isThisBusy && busyAction === 'download' ? 'Preparing…' : 'Download'}
                                </button>
                              )}
                              {canBuild && engineUpgradeRequired && (
                                <span className="muted" style={{ fontSize: 11, fontStyle: 'italic' }}>Upgrade engine first</span>
                              )}
                              {canBuild && !engineUpgradeRequired && (
                                <button type="button" onClick={() => handleBuild(platform)} className="btn sm">
                                  {buildState === 'Failed'
                                    ? 'Retry build'
                                    : buildState === 'Ready'
                                    ? `Update to v${projectVersion}`
                                    : `Build v${projectVersion}`}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {failures[platform] && <InlineError>{failures[platform]}</InlineError>}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {!macArchPrompt && (
          <div className="muted" style={{ fontSize: 12, marginTop: 16, lineHeight: 1.5 }}>
            Builds always target the active version. To build another, switch it in{' '}
            <button
              type="button"
              onClick={onGoToVersionControl}
              className="nav-link"
              style={{ padding: 0, height: 'auto', display: 'inline', color: 'var(--accent)' }}
            >
              version control
            </button>
            .
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button type="button" onClick={onClose} className="btn">
            Close
          </button>
        </div>
      </div>

      {comingSoonPlatform && (
        <div className="modal-backdrop" onClick={() => setComingSoonPlatform(null)} style={{ zIndex: 90 }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Heads up</div>
            <h3 className="display tight" style={{ fontSize: 22, margin: '0 0 10px' }}>
              Coming soon
            </h3>
            <p className="muted-2" style={{ fontSize: 13.5, lineHeight: 1.55, margin: '0 0 22px' }}>
              {PLATFORM_META[comingSoonPlatform]?.name} builds aren't available yet.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setComingSoonPlatform(null)} className="btn primary">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
